import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { FileText, Download, X, Image as ImageIcon, Plus, Music2, Folder as FolderIcon, MoreVertical, BookOpen } from 'lucide-react';
import mapi from './api';
import { MobileShell, MobileHeader, MobileSheet, C, FilterChips } from './MobileShell';
import { matchesFreeText } from '@/components/search/SearchBuilder';

function sizeFmt(b) {
  if (!b) return '';
  return b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
}

function previewKind(d) {
  const mime = (d?.mime_type || '').toLowerCase();
  const name = (d?.filename || '').toLowerCase();
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif)$/.test(name)) return 'image';
  if (mime.startsWith('audio/') || name.endsWith('.mp3')) return 'audio';
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  return null;
}

function isFolder(d) {
  return d?.kind === 'folder';
}

async function blobErrorMessage(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.detail === 'string' && parsed.detail.trim()) return parsed.detail;
    } catch { /* ignore */ }
  }
  if (typeof err?.response?.data?.detail === 'string') return err.response.data.detail;
  if (err?.code === 'ERR_NETWORK') return 'Keine Verbindung zum Server';
  return 'Datei konnte nicht geladen werden';
}

async function fetchDocBlob(id) {
  const r = await mapi.get(`/app/documents/${id}/download`, {
    responseType: 'blob', timeout: 60000, params: { inline: true },
  });
  const type = (r.data?.type || '').toLowerCase();
  if (type.includes('json') || type.includes('text/html')) {
    const err = new Error('Datei nicht gefunden');
    err.response = { data: r.data };
    throw err;
  }
  return r.data;
}

export default function MobileDocuments({ area = 'public' }) {
  const isFiles = area === 'personal';
  const [docs, setDocs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [folderTitle, setFolderTitle] = useState('');
  const [folderId, setFolderId] = useState(null);
  const [crumbs, setCrumbs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [actionDoc, setActionDoc] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [shareDoc, setShareDoc] = useState(null);
  const [shareTargets, setShareTargets] = useState({ students: [], teachers: [], courses: [] });
  const [pickedShares, setPickedShares] = useState([]);
  const [shareQuery, setShareQuery] = useState('');
  const [fileFilter, setFileFilter] = useState('alle');
  const [fileQuery, setFileQuery] = useState('');
  const [filesSeen, setFilesSeen] = useState(null);
  const fileRef = useRef(null);
  const folderRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const note = await mapi.get('/app/notifications').catch(() => ({ data: {} }));
      setFilesSeen(note.data?.files_seen || null);
      const r = await mapi.get('/app/documents');
      const all = r.data || [];
      setDocs(isFiles
        ? all.filter((d) => d.audience && d.audience !== 'public')
        : all.filter((d) => d.kind !== 'folder' && (
          (!d.audience || d.audience === 'public') || d.shared_with_me
        )));
      await mapi.post('/app/notifications/seen', { area: 'files' }).catch(() => {});
    } catch (e) { toast.error('Dokumente konnten nicht geladen werden'); }
  }, [isFiles]);
  useEffect(() => { load(); }, [load]);

  const kind = previewKind(detail);

  useEffect(() => {
    if (!detail?.filename || !kind) {
      setPreviewUrl(null);
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl;
    setPreviewLoading(true);
    setPreviewUrl(null);
    fetchDocBlob(detail.id)
      .then((blob) => {
        if (cancelled) return;
        const typed = new Blob([blob], { type: detail.mime_type || blob.type || 'application/octet-stream' });
        objectUrl = URL.createObjectURL(typed);
        setPreviewUrl(objectUrl);
      })
      .catch(async (err) => {
        if (!cancelled) toast.error(await blobErrorMessage(err) || 'Vorschau fehlgeschlagen');
      })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [detail, kind]);

  const matchesQuery = (d) => matchesFreeText(d, fileQuery);
  const isNewShare = (d) => {
    if (!d.shared_with_me) return false;
    if (!filesSeen) return true;
    const created = Date.parse(d.created_at || '');
    const seen = Date.parse(filesSeen);
    return !Number.isNaN(created) && !Number.isNaN(seen) && created > seen;
  };
  const here = docs.filter((d) => (d.parent_id || null) === (folderId || null)).filter(matchesQuery);
  const folders = here.filter(isFolder).sort((a, b) => (a.title || '').localeCompare(b.title || '', 'de'));
  const filesHere = here.filter((d) => !isFolder(d)).filter((d) => {
    if (fileFilter === 'freigaben') return !!d.shared_with_me;
    if (fileFilter === 'eigene') return !d.shared_with_me;
    if (fileFilter === 'ordner') return false;
    return true;
  });
  const shownFolders = fileFilter === 'freigaben' || fileFilter === 'eigene' ? [] : folders;

  const groups = {};
  docs.forEach((d) => {
    const g = new Date(d.created_at).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    (groups[g] = groups[g] || []).push(d);
  });

  const fmt = (d) => new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const download = async (d) => {
    try {
      const blob = await fetchDocBlob(d.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = d.filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(await blobErrorMessage(e)); }
  };

  const close = () => setDetail(null);

  const openFolder = (d) => {
    setFolderId(d.id);
    setCrumbs((c) => [...c, { id: d.id, title: d.title }]);
  };

  const goCrumb = (id) => {
    if (!id) {
      setFolderId(null);
      setCrumbs([]);
      return;
    }
    const idx = crumbs.findIndex((c) => c.id === id);
    setFolderId(id);
    setCrumbs((c) => (idx < 0 ? c : c.slice(0, idx + 1)));
  };

  const closeSheet = () => {
    setSheet(null);
    setUploadFile(null);
    setUploadTitle('');
    setFolderTitle('');
  };

  const pickUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadFile(f);
    setUploadTitle((t) => (t.trim() ? t : f.name.replace(/\.[^.]+$/, '')));
  };

  const pickFolder = async (e) => {
    const list = [...(e.target.files || [])];
    e.target.value = '';
    if (!list.length) return;
    setSaving(true);
    try {
      const fd = new FormData();
      if (folderId) fd.append('parent_id', folderId);
      list.forEach((f) => {
        fd.append('files', f);
        fd.append('paths', f.webkitRelativePath || f.name);
      });
      const r = await mapi.post('/app/documents/tree', fd, { timeout: 120000 });
      const created = r.data?.created ?? list.length;
      const skipped = r.data?.skipped || 0;
      toast.success(skipped
        ? `${created} Dateien übernommen, ${skipped} übersprungen`
        : `${created} Dateien übernommen`);
      closeSheet();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ordner-Upload fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Datei wählen');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', (uploadTitle || uploadFile.name).trim());
      fd.append('file', uploadFile);
      if (folderId) fd.append('parent_id', folderId);
      await mapi.post('/app/documents', fd, { timeout: 60000 });
      toast.success('Datei hochgeladen — sichtbar für das Personal');
      closeSheet();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Hochladen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const submitFolder = async (e) => {
    e.preventDefault();
    const title = folderTitle.trim();
    if (!title) {
      toast.error('Ordnername fehlt');
      return;
    }
    setSaving(true);
    try {
      await mapi.post('/app/documents/folders', { title, parent_id: folderId || null });
      toast.success('Ordner angelegt');
      closeSheet();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Ordner konnte nicht angelegt werden');
    } finally {
      setSaving(false);
    }
  };

  const loadShareTargets = async (q) => {
    const r = await mapi.get('/app/documents/share-targets', {
      params: q ? { q } : undefined,
    });
    setShareTargets(r.data || { students: [], teachers: [], courses: [] });
  };

  const openShare = async (doc) => {
    setActionDoc(null);
    setConfirmDelete(false);
    setShareDoc(doc);
    setShareQuery('');
    setPickedShares((doc.shares || []).map((s) => `${s.target_type}:${s.target_id}`));
    try {
      await loadShareTargets('');
    } catch (err) {
      toast.error('Empfänger konnten nicht geladen werden');
    }
  };

  const onShareQuery = async (value) => {
    setShareQuery(value);
    try {
      await loadShareTargets(value.trim());
    } catch (err) {
      toast.error('Empfänger konnten nicht geladen werden');
    }
  };

  const toggleShare = (type, id) => {
    const key = `${type}:${id}`;
    setPickedShares((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const saveShares = async () => {
    if (!shareDoc) return;
    setSaving(true);
    try {
      const shares = pickedShares.map((key) => {
        const splitAt = key.indexOf(':');
        return { target_type: key.slice(0, splitAt), target_id: key.slice(splitAt + 1) };
      });
      const r = await mapi.put(`/app/documents/${shareDoc.id}/shares`, { shares });
      setDocs((prev) => prev.map((row) => (
        row.id === shareDoc.id
          ? { ...row, shares: r.data?.shares || [], share_line: r.data?.share_line || '' }
          : row
      )));
      setShareDoc(null);
      toast.success(r.data?.share_line ? 'Freigabe gespeichert' : 'Freigabe entzogen');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Freigabe fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const deleteDoc = async (doc) => {
    setSaving(true);
    try {
      await mapi.delete(`/app/documents/${doc.id}`);
      setDocs((prev) => prev.filter((row) => row.id !== doc.id));
      setActionDoc(null);
      setConfirmDelete(false);
      if (detail?.id === doc.id) setDetail(null);
      toast.success('Datei gelöscht');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const renderFileRow = (d) => {
    const kindIcon = previewKind(d);
    const Icon = kindIcon === 'image' ? ImageIcon : kindIcon === 'audio' ? Music2 : FileText;
    return (
      <div key={d.id}
           className="w-full text-left rounded-xl mb-3 px-4 py-3.5 flex items-start gap-2"
           style={{ background: C.card, border: `1px solid ${C.border}` }}>
        <button type="button" onClick={() => setDetail(d)}
                data-testid={`mobile-doc-${d.id}`}
                className="flex flex-1 min-w-0 items-start gap-3 text-left">
          <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: C.red }} />
          {isNewShare(d) && (
            <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: C.red }}
                  data-testid={`mobile-doc-new-${d.id}`} />
          )}
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-semibold truncate" style={{ color: C.text }}>{d.title}</span>
            <span className="block text-[11px]" style={{ color: C.dim }}>{fmt(d.created_at)}{d.size_bytes ? ` · ${sizeFmt(d.size_bytes)}` : ''}</span>
            {d.share_line && (
              <span data-testid={`mobile-doc-share-line-${d.id}`}
                    className="block text-xs mt-1" style={{ color: C.danger }}>
                {d.share_line}
              </span>
            )}
            {d.description && <span className="block text-xs mt-1 line-clamp-2" style={{ color: C.dim }}>{d.description}</span>}
          </span>
        </button>
        {isFiles && (
          <button type="button" aria-label="Aktionen"
                  data-testid={`mobile-doc-actions-${d.id}`}
                  className="shrink-0 rounded-full p-1.5"
                  style={{ color: C.dim }}
                  onClick={() => { setActionDoc(d); setConfirmDelete(false); }}>
            <MoreVertical className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const emptyFiles = isFiles
    ? (!shownFolders.length && !filesHere.length)
    : !docs.length;

  return (
    <MobileShell onAction={isFiles ? () => setSheet('menu') : undefined}>
      <MobileHeader
        title={isFiles ? (crumbs.length ? crumbs[crumbs.length - 1].title : 'ShareSpace') : 'Dokumente'}
        onRefresh={load}
        backTo="/m"
        right={isFiles ? (
          <button
            type="button"
            onClick={() => setSheet('menu')}
            data-testid="mobile-files-upload-btn"
            className="p-1.5 rounded-full"
            style={{ color: '#fff' }}
            aria-label="Hinzufügen"
          >
            <Plus className="w-5 h-5" />
          </button>
        ) : null}
      />
      <div className="flex-1 overflow-y-auto px-4 py-3 pb-10">
        {isFiles && (
          <p className="text-xs mb-3 px-1" style={{ color: C.dim }} data-testid="mobile-files-hint">
            Materialien und Austausch zwischen Lehrkraft und Schüler: Noten, Aufnahmen, PDFs und Übungsmaterial.
          </p>
        )}
        {isFiles && (
          <div className="flex items-center gap-2 rounded-lg px-3 py-2 mb-3"
               style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <input value={fileQuery} onChange={(e) => setFileQuery(e.target.value)} placeholder="Suchen…"
                   data-testid="mobile-files-search"
                   className="flex-1 bg-transparent text-sm outline-none" style={{ color: C.text }} />
          </div>
        )}
        {isFiles && (
          <FilterChips
            testIdPrefix="mobile-files-filter"
            value={fileFilter}
            onChange={setFileFilter}
            items={[
              { k: 'alle', label: 'Alle' },
              { k: 'freigaben', label: 'Freigaben' },
              { k: 'eigene', label: 'Eigene' },
              { k: 'ordner', label: 'Ordner' },
            ]}
          />
        )}
        {isFiles && (
          <div className="flex flex-wrap items-center gap-1 text-xs mb-3 px-1" style={{ color: C.dim }}>
            <button type="button" onClick={() => goCrumb(null)} data-testid="mobile-files-crumb-root"
                    className="font-semibold" style={{ color: crumbs.length ? C.red : C.dim }}>
              Dateien
            </button>
            {crumbs.map((c) => (
              <span key={c.id} className="flex items-center gap-1">
                <span>/</span>
                <button type="button" onClick={() => goCrumb(c.id)} data-testid={`mobile-files-crumb-${c.id}`}
                        className="font-semibold truncate max-w-[8rem]" style={{ color: C.text }}>
                  {c.title}
                </button>
              </span>
            ))}
          </div>
        )}
        {emptyFiles && (
          <p className="text-center text-sm mt-10" style={{ color: C.dim }}>
            {isFiles ? (folderId ? 'Dieser Ordner ist leer' : 'Keine Dateien vorhanden') : 'Keine Dokumente vorhanden'}
          </p>
        )}
        {isFiles && shownFolders.map((d) => (
          <button key={d.id} type="button" onClick={() => openFolder(d)}
                  data-testid={`mobile-folder-${d.id}`}
                  className="w-full text-left rounded-xl mb-3 px-4 py-3.5 flex items-start gap-3"
                  style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <FolderIcon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: C.red }} />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold truncate" style={{ color: C.text }}>{d.title}</span>
              <span className="block text-[11px]" style={{ color: C.dim }}>Ordner</span>
            </span>
          </button>
        ))}
        {isFiles && filesHere.map(renderFileRow)}
        {isFiles && !folderId && ['lehrer', 'personal'].includes(localStorage.getItem('app_area') || '') && (
          <Link
            to="/m/logbuch"
            data-testid="mobile-logbuch-entry"
            className="mt-2 flex items-center gap-3 rounded-xl px-4 py-3"
            style={{ background: C.rose }}
          >
            <BookOpen className="h-6 w-6 shrink-0" style={{ color: C.red }} />
            <span className="min-w-0">
              <span className="block text-sm font-bold" style={{ color: C.text }}>Logbuch</span>
              <span className="block text-xs" style={{ color: C.dim }}>
                Notizen, Aufgaben und Lernfortschritte
              </span>
            </span>
          </Link>
        )}
        {!isFiles && Object.entries(groups).map(([g, list]) => (
          <div key={g} className="mb-4">
            <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: C.red }}>{g}</p>
            {list.map(renderFileRow)}
          </div>
        ))}
      </div>

      {isFiles && (
        <input
          ref={folderRef}
          type="file"
          multiple
          className="hidden"
          data-testid="mobile-files-folder-input"
          onChange={pickFolder}
          {...{ webkitdirectory: '', directory: '' }}
        />
      )}

      {sheet === 'menu' && (
        <MobileSheet scrim="rgba(0,0,0,0.55)" onClick={closeSheet}>
          <div className="w-full max-w-md rounded-t-2xl p-5 space-y-2" style={{ background: C.card }}
               onClick={(e) => e.stopPropagation()} data-testid="mobile-files-action-menu">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold" style={{ color: C.text }}>Hinzufügen</h3>
              <button type="button" onClick={closeSheet}><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            <button type="button" data-testid="mobile-files-action-file"
                    onClick={() => setSheet('file')}
                    className="w-full rounded-xl py-3 text-sm text-left px-4"
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}>
              Datei hochladen
            </button>
            <button type="button" data-testid="mobile-files-action-folder-upload"
                    onClick={() => folderRef.current?.click()}
                    className="w-full rounded-xl py-3 text-sm text-left px-4"
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}>
              Ordner hochladen
            </button>
            <button type="button" data-testid="mobile-files-action-folder-create"
                    onClick={() => setSheet('folder')}
                    className="w-full rounded-xl py-3 text-sm text-left px-4"
                    style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}>
              Ordner anlegen
            </button>
          </div>
        </MobileSheet>
      )}

      {sheet === 'file' && (
        <MobileSheet scrim="rgba(0,0,0,0.55)" onClick={closeSheet}>
          <form
            onSubmit={submitUpload}
            data-testid="mobile-files-upload-form"
            className="w-full max-w-md rounded-t-2xl p-5 space-y-3"
            style={{ background: C.card }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold" style={{ color: C.text }}>Datei hochladen</h3>
              <button type="button" onClick={closeSheet} data-testid="mobile-files-upload-close">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            <p className="text-xs" style={{ color: C.dim }}>PDF, JPG, PNG oder MP3, max. 25 MB. Sichtbar für alle Lehrkräfte.</p>
            <label className="block text-[11px] font-semibold" style={{ color: C.dim }}>
              Titel
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                className="w-full mt-1 rounded-lg px-3 py-2.5 text-sm outline-none"
                style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                placeholder="z. B. Noten Klasse 3"
                data-testid="mobile-files-upload-title"
              />
            </label>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.mp3,audio/mpeg"
              className="hidden"
              onChange={pickUpload}
              data-testid="mobile-files-upload-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="mobile-files-upload-pick"
              className="w-full rounded-xl py-2.5 text-sm"
              style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
            >
              {uploadFile ? uploadFile.name : 'Datei wählen'}
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="mobile-files-upload-save"
              className="w-full rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-50"
              style={{ background: C.red }}
            >
              {saving ? 'Hochladen…' : 'Hochladen'}
            </button>
          </form>
        </MobileSheet>
      )}

      {sheet === 'folder' && (
        <MobileSheet scrim="rgba(0,0,0,0.55)" onClick={closeSheet}>
          <form
            onSubmit={submitFolder}
            data-testid="mobile-files-folder-form"
            className="w-full max-w-md rounded-t-2xl p-5 space-y-3"
            style={{ background: C.card }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold" style={{ color: C.text }}>Ordner anlegen</h3>
              <button type="button" onClick={closeSheet}>
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            <label className="block text-[11px] font-semibold" style={{ color: C.dim }}>
              Name
              <input
                type="text"
                value={folderTitle}
                onChange={(e) => setFolderTitle(e.target.value)}
                className="w-full mt-1 rounded-lg px-3 py-2.5 text-sm outline-none"
                style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
                placeholder="z. B. Noten"
                data-testid="mobile-files-folder-title"
              />
            </label>
            <button
              type="submit"
              disabled={saving}
              data-testid="mobile-files-folder-save"
              className="w-full rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-50"
              style={{ background: C.red }}
            >
              {saving ? 'Anlegen…' : 'Anlegen'}
            </button>
          </form>
        </MobileSheet>
      )}

      {actionDoc && (
        <MobileSheet
          scrim="rgba(0,0,0,0.45)"
          onClick={() => { setActionDoc(null); setConfirmDelete(false); }}
          data-testid="mobile-doc-action-sheet"
        >
          <div className="w-full max-w-md rounded-t-2xl p-4 space-y-2" style={{ background: C.card }}
               onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold truncate pr-3">{actionDoc.title}</p>
              <button type="button" onClick={() => { setActionDoc(null); setConfirmDelete(false); }} aria-label="Schließen">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            {!confirmDelete && (
              <>
                <button type="button" data-testid="mobile-doc-action-preview"
                        className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                        style={{ background: C.bg, border: `1px solid ${C.border}` }}
                        onClick={() => { setDetail(actionDoc); setActionDoc(null); }}>
                  Ansehen
                </button>
                <button type="button" data-testid="mobile-doc-action-share"
                        className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                        style={{ background: C.bg, border: `1px solid ${C.border}` }}
                        onClick={() => openShare(actionDoc)}>
                  Teilen
                </button>
                <button type="button" data-testid="mobile-doc-action-edit-share"
                        className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                        style={{ background: C.bg, border: `1px solid ${C.border}` }}
                        onClick={() => openShare(actionDoc)}>
                  Freigabe bearbeiten
                </button>
                <button type="button" data-testid="mobile-doc-action-delete"
                        className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold"
                        style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.danger }}
                        onClick={() => setConfirmDelete(true)}>
                  Löschen
                </button>
              </>
            )}
            {confirmDelete && (
              <>
                <p className="text-sm" style={{ color: C.text }}>
                  Die Datei wird gelöscht. Eine Freigabe entziehen lässt die Datei bestehen.
                </p>
                <button type="button" data-testid="mobile-doc-delete-confirm"
                        disabled={saving}
                        className="w-full rounded-xl px-3 py-3 text-sm font-bold text-white disabled:opacity-50"
                        style={{ background: C.danger }}
                        onClick={() => deleteDoc(actionDoc)}>
                  Endgültig löschen
                </button>
              </>
            )}
          </div>
        </MobileSheet>
      )}

      {shareDoc && (
        <MobileSheet scrim="rgba(0,0,0,0.45)" onClick={() => setShareDoc(null)} data-testid="mobile-doc-share-sheet">
          <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl p-4 space-y-3"
               style={{ background: C.card }} onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">Freigabe</p>
              <button type="button" onClick={() => setShareDoc(null)} aria-label="Schließen">
                <X className="w-5 h-5" style={{ color: C.dim }} />
              </button>
            </div>
            <input
              type="text"
              value={shareQuery}
              onChange={(e) => onShareQuery(e.target.value)}
              placeholder="Person oder Gruppe suchen"
              data-testid="mobile-doc-share-search"
              className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
              style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}
            />
            {[
              ['students', 'Schüler:innen', 'student'],
              ['teachers', 'Lehrkräfte', 'teacher'],
              ['courses', 'Gruppen', 'course'],
            ].map(([key, heading, type]) => (
              <div key={key}>
                <p className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: C.dim }}>{heading}</p>
                {(shareTargets[key] || []).length === 0 ? (
                  <p className="text-xs" style={{ color: C.dim }}>Keine Einträge</p>
                ) : (shareTargets[key] || []).map((target) => {
                  const on = pickedShares.includes(`${type}:${target.id}`);
                  const extra = type === 'course' && target.participant_count
                    ? ` · ${target.participant_count} Teilnehmer`
                    : '';
                  return (
                    <button key={target.id} type="button"
                            data-testid={`mobile-share-target-${type}-${target.id}`}
                            className="mb-1 w-full rounded-xl px-3 py-2.5 text-left text-sm"
                            style={{
                              background: on ? '#FCEEEE' : C.bg,
                              border: `1px solid ${on ? C.danger : C.border}`,
                              color: C.text,
                            }}
                            onClick={() => toggleShare(type, target.id)}>
                      {target.label}{extra}
                    </button>
                  );
                })}
              </div>
            ))}
            <button type="button" data-testid="mobile-doc-share-save"
                    disabled={saving}
                    className="w-full rounded-xl py-3 text-sm font-bold text-white disabled:opacity-50"
                    style={{ background: C.danger }}
                    onClick={saveShares}>
              {saving ? 'Speichern…' : 'Freigabe speichern'}
            </button>
          </div>
        </MobileSheet>
      )}

      {detail && (
        <MobileSheet scrim="rgba(0,0,0,0.75)" onClick={close}>
          <div className="w-full max-w-md max-h-[92vh] flex flex-col rounded-t-2xl overflow-hidden"
               style={{ background: C.card }} onClick={(e) => e.stopPropagation()} data-testid="mobile-doc-detail">
            <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
              <h3 className="font-bold pr-4 truncate" style={{ color: C.text }}>{detail.title}</h3>
              <button onClick={close} data-testid="mobile-doc-close"><X className="w-5 h-5" style={{ color: C.dim }} /></button>
            </div>
            {detail.description && (
              <p className="text-sm whitespace-pre-wrap px-5 pb-3 shrink-0" style={{ color: '#c9c9d1' }}>{detail.description}</p>
            )}

            {detail.filename && kind && (
              <div className="flex-1 min-h-[220px] mx-4 mb-3 rounded-xl overflow-hidden flex items-center justify-center"
                   style={{ background: '#0e0e11', border: `1px solid ${C.border}` }}
                   data-testid="mobile-doc-preview">
                {previewLoading && (
                  <p className="text-sm" style={{ color: C.dim }}>Vorschau wird geladen…</p>
                )}
                {!previewLoading && previewUrl && kind === 'image' && (
                  <img src={previewUrl} alt={detail.title}
                       className="max-w-full max-h-[58vh] object-contain" />
                )}
                {!previewLoading && previewUrl && kind === 'pdf' && (
                  <iframe title={detail.title} src={previewUrl}
                          className="w-full h-[58vh] border-0 bg-white" />
                )}
                {!previewLoading && previewUrl && kind === 'audio' && (
                  <audio
                    controls
                    preload="metadata"
                    src={previewUrl}
                    className="w-full px-3"
                    data-testid="mobile-doc-audio"
                  >
                    Ihr Browser kann diese Audiodatei nicht wiedergeben.
                  </audio>
                )}
                {!previewLoading && !previewUrl && (
                  <p className="text-sm px-4 text-center" style={{ color: C.dim }}>Vorschau nicht verfügbar</p>
                )}
              </div>
            )}

            <div className="px-5 pb-6 shrink-0 space-y-2">
              {detail.filename && (
                <button onClick={() => download(detail)} data-testid="mobile-doc-download"
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold"
                        style={{ background: 'transparent', border: `1px solid ${C.border}`, color: C.text }}>
                  <Download className="w-4 h-4" /> Speichern ({sizeFmt(detail.size_bytes)})
                </button>
              )}
              {!detail.filename && (
                <p className="text-center text-sm py-2" style={{ color: C.dim }}>Keine Datei hinterlegt</p>
              )}
            </div>
          </div>
        </MobileSheet>
      )}
    </MobileShell>
  );
}
